import React, { useState, useEffect, useMemo, useCallback } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import { Plus, Edit, Trash2, X, Search, RefreshCw, FileX, AlertCircle, CheckSquare, Eye, Download, } from "lucide-react";
import { BASE_URL } from "../Apis/apiConfig";
import "../Style/Dashboard.css";
import "../Style/FinancialTables.css";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import Swal from "sweetalert2";

Modal.setAppElement("#root");

const Vouchers = () => {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loadingIds, setLoadingIds] = useState(() => new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [selectedVouchers, setSelectedVouchers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const fetchVouchers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const societyName = localStorage.getItem("society") ?? "";
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await fetch(
        `${BASE_URL}/vouchers/get-voucher-by-member?societyName=${encodeURIComponent(
          societyName,
        )}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...headers,
          },
        },
      );
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();
      setVouchers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("fetchVouchers error:", err);
      setError("Failed to load vouchers");
      setVouchers([]);
    } finally {
      setLoading(false);
      setCurrentPage(1);
    }
  }, []);

  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);


  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return vouchers;

    const searchLower = searchTerm.toLowerCase();
    return vouchers.filter((voucher) =>
      Object.values(voucher).some((value) =>
        String(value).toLowerCase().includes(searchLower),
      ),
    );
  }, [vouchers, searchTerm]);


  const totalItems = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [perPage, totalItems, currentPage, totalPages]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredData.slice(start, start + perPage);
  }, [filteredData, currentPage, perPage]);

  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

 
  const renderPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      for (let p = 1; p <= totalPages; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }
    } else {
      let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
      let end = Math.min(totalPages, start + maxButtons - 1);

      if (end - start < maxButtons - 1) {
        start = Math.max(1, end - maxButtons + 1);
      }

      if (start > 1) {
        pages.push(
          <button
            key={1}
            className={`pagination-btn ${1 === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>,
        );
        if (start > 2) {
          pages.push(
            <span key="start-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
      }

      for (let p = start; p <= end; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }

      if (end < totalPages) {
        if (end < totalPages - 1) {
          pages.push(
            <span key="end-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
        pages.push(
          <button
            key={totalPages}
            className={`pagination-btn ${
              totalPages === currentPage ? "active" : ""
            }`}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>,
        );
      }
    }

    return pages;
  };

  const handleDeleteVoucher = async (
    voucherId,
    unitNo,
    societyName,
    voucherCode,
  ) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this voucher?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setLoadingIds((prev) => new Set([...prev, voucherId]));

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}/vouchers/delete-voucher-by-member?voucherId=${encodeURIComponent(
          voucherId,
        )}&unitNo=${encodeURIComponent(
          unitNo,
        )}&societyName=${encodeURIComponent(societyName)}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }

      if (!response.ok)
        throw new Error(data?.message || "Failed to delete voucher");

      setVouchers((prev) =>
        prev.filter(
          (v) => v.voucherCode !== voucherCode && v.voucherId !== voucherId,
        ),
      );
      Swal.fire("Deleted!", "Voucher deleted successfully!", "success");
    } catch (err) {
      console.error("Delete voucher error:", err);
      Swal.fire("Error", err.message || "Failed to delete voucher", "error");
    } finally {
      setLoadingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(voucherId);
        return newSet;
      });
    }
  };

  const deleteSelectedMembers = async () => {
    if (selectedVouchers.length === 0) {
      Swal.fire("Info", "Please select vouchers to delete", "info");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `This will delete ${selectedVouchers.length} selected voucher(s)!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("token");
      const societyName = localStorage.getItem("society") || "";

      const deletePromises = selectedVouchers.map((voucherId) => {
        const voucher = vouchers.find((v) => v.voucherId === voucherId);
        if (!voucher) return Promise.resolve();

        return fetch(
          `${BASE_URL}/vouchers/delete-voucher-by-member?voucherId=${encodeURIComponent(
            voucher.voucherId,
          )}&unitNo=${encodeURIComponent(
            voucher.unitNo,
          )}&societyName=${encodeURIComponent(voucher.societyName)}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      });

      await Promise.all(deletePromises);
      setVouchers((prev) =>
        prev.filter((v) => !selectedVouchers.includes(v.voucherId)),
      );
      setSelectedVouchers([]);
      Swal.fire("Deleted!", "Selected vouchers have been deleted!", "success");
    } catch (err) {
      console.error("Delete selected vouchers error:", err);
      Swal.fire("Error", "Failed to delete selected vouchers", "error");
    }
  };

  const deleteByAdminBillTable = async () => {
    const token = localStorage.getItem("token");
    const societyName = localStorage.getItem("society");
    if (!societyName) {
      Swal.fire("Error", "Society name is required", "error");
      return;
    }

    const result = await Swal.fire({
      title: "Are you absolutely sure?",
      text: `This will permanently delete ALL vouchers for "${societyName}"!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete all",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
     
      const deletePromises = vouchers.map((voucher) =>
        fetch(
          `${BASE_URL}/vouchers/delete-voucher-by-member?voucherId=${encodeURIComponent(
            voucher.voucherId,
          )}&unitNo=${encodeURIComponent(
            voucher.unitNo,
          )}&societyName=${encodeURIComponent(voucher.societyName)}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
      );

      await Promise.all(deletePromises);
      setVouchers([]);
      setSelectedVouchers([]);
      Swal.fire("Deleted!", "All vouchers have been deleted!", "success");
    } catch (err) {
      console.error("Bulk delete error:", err);
      Swal.fire("Error", "Failed to delete vouchers", "error");
    }
  };

  const selectAllRows = (e) => {
    if (e.target.checked) {
      const allIds = paginatedData
        .map((item) => item.voucherId)
        .filter((id) => id !== undefined);
      setSelectedVouchers(allIds);
    } else {
      setSelectedVouchers([]);
    }
  };

  const handleRowSelect = (id) => {
    if (!id) return;

    setSelectedVouchers((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleEditClick = (voucher) => {
    setEditFormData(voucher);
    setIsEditModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem("token");
      const societyName = localStorage.getItem("society");

      const response = await fetch(
        `${BASE_URL}/vouchers/edit-voucher-by-admin-society`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...editFormData,
            societyName,
          }),
        },
      );

      if (!response.ok) throw new Error("Failed to update voucher");

      Swal.fire("Success", "Voucher updated successfully!", "success");
      setIsEditModalOpen(false);
      fetchVouchers();
    } catch (err) {
      console.error("Edit voucher error:", err);
      Swal.fire("Error", err.message || "Failed to update voucher", "error");
    }
  };

  const handleViewItem = (item) => {
    Swal.fire({
      title: "Voucher Details",
      html: `
        <div style="text-align: left; font-family: monospace;">
          <div style="margin-bottom: 8px;">
            <strong>Company:</strong> ${item.companyName || "-"}
          </div>
          <div style="margin-bottom: 8px;">
            <strong>Voucher Code:</strong> ${item.voucherCode || "-"}
          </div>
          <div style="margin-bottom: 8px;">
            <strong>Employee:</strong> ${item.employeeName || "-"}
          </div>
          <div style="margin-bottom: 8px;">
            <strong>Society:</strong> ${item.societyName || "-"}
          </div>
          <div style="margin-bottom: 8px;">
            <strong>Unit No:</strong> ${item.unitNo || "-"}
          </div>
          <div style="margin-bottom: 8px;">
            <strong>Start Date:</strong> ${item.startDate || "-"}
          </div>
          <div style="margin-bottom: 8px;">
            <strong>End Date:</strong> ${item.endDate || "-"}
          </div>
        </div>
      `,
      width: "500px",
      confirmButtonText: "Close",
    });
  };

  const handleAddClick = () => navigate("/add-vouchers");

  return (
    <div className="app-container">
      <style>
        {`
/* ========== VOUCHERS PAGE OPTIMIZATIONS ========== */
.app-container {
  height: 100vh;
  overflow: hidden !important;
}

.main-layout {
  height: calc(100vh - 64px);
  overflow: hidden !important;
}

.content-area {
  height: 100%;
  overflow-y: auto !important;
  overflow-x: hidden;
  padding-bottom: 20px;
}

.financial-container,
.financial-card {
  height: auto !important;
  overflow: visible !important;
  margin: 0 !important;
  padding: 0 !important;
}

.financial-body {
  height: auto !important;
  overflow: visible !important;
  padding: 0 !important;
}

/* ========== COMPACT TABLE CONTROLS ========== */
.controls-left,
.controls-center,
.controls-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.controls-center {
  flex: 1;
  justify-content: center;
}

.entries-selector label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.entries-select {
  padding: 4px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background: white;
  font-size: 12px;
  color: #334155;
  cursor: pointer;
  height: 28px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
}

.pagination-nav {
  padding: 4px 10px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  color: #334155;
  font-size: 11px;
  font-weight: 500;
  height: 26px;
  transition: all 0.2s;
}

.pagination-nav:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-nav:not(:disabled):hover {
  background: #f1f5f9;
}

.pagination-btn {
  min-width: 26px;
  height: 26px;
  padding: 4px 8px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  color: #334155;
  font-size: 11px;
  font-weight: 500;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-btn:hover {
  background: #f1f5f9;
}

.pagination-btn.active {
  background: #0d6efd;
  color: white;
  border-color: #0d6efd;
}

.pagination-ellipsis {
  padding: 0 4px;
  color: #94a3b8;
  font-size: 11px;
}

.entries-info {
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
}

/* ========== TABLE OPTIMIZATION ========== */
.financial-table-container {
  overflow-x: auto;
  padding: 0;
  margin: 0;
}

.financial-table,
.data-table,
.vouchers-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
}

.financial-table th,
.data-table th,
.vouchers-table th {
  padding: 6px 8px !important;
  text-align: center !important;
  vertical-align: middle;
  white-space: nowrap;
  font-weight: 600;
  font-size: 11px !important;
  background-color: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  line-height: 1.2 !important;
  height: 32px !important;
}

.financial-table td,
.data-table td,
.vouchers-table td {
  padding: 5px 8px !important;
  text-align: center !important;
  vertical-align: middle;
  font-size: 10px !important;
  border-bottom: 1px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  line-height: 1.3 !important;
  height: 36px !important;
}

/* Checkbox Column */
.financial-table th:nth-child(1),
.financial-table td:nth-child(1),
.data-table th:first-child,
.data-table td:first-child,
.vouchers-table th:first-child,
.vouchers-table td:first-child {
  width: 35px !important;
  min-width: 35px !important;
  max-width: 35px !important;
  padding: 5px 2px !important;
}

/* Actions Column */
.financial-table th:last-child,
.financial-table td:last-child,
.data-table th:last-child,
.data-table td:last-child,
.vouchers-table th:last-child,
.vouchers-table td:last-child {
  width: 120px !important;
  min-width: 120px !important;
  padding: 4px 2px !important;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin: 0;
}

.action-icon-btn,
.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 3px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  width: 24px;
  height: 24px;
}

.action-icon-btn svg,
.icon-btn svg {
  width: 14px;
  height: 14px;
}

.action-icon-btn:hover,
.icon-btn:hover {
  background-color: #f1f5f9;
}

.icon-btn.edit {
  color: #0d6efd;
}

/* Checkbox */
.financial-table input[type="checkbox"],
.data-table input[type="checkbox"],
.vouchers-table input[type="checkbox"] {
  margin: 0;
  width: 13px;
  height: 13px;
  vertical-align: middle;
  cursor: pointer;
}

/* Row Hover */
.financial-table tbody tr:hover td,
.data-table tbody tr:hover td,
.vouchers-table tbody tr:hover td {
  background-color: #f8fafc;
}

.selected-row td {
  background-color: #e3f2fd !important;
}

/* Cell Content */
.cell-content {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

/* Cell Ellipsis */
.cell-ellipsis {
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Empty State */
.financial-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  min-height: 300px;
}

.financial-state h3 {
  margin: 16px 0 8px 0;
  color: #334155;
  font-size: 18px;
  font-weight: 600;
}

.financial-state p {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

/* Loading Animation */
.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
  .controls-left,
  .controls-center,
  .controls-right {
    width: 100%;
    justify-content: space-between;
  }
}

/* Specific for vouchers table */
.vouchers-table.compact-table thead th {
  white-space: normal !important;
  text-align: center;
  vertical-align: middle;
  line-height: 1.15;
  padding: 6px 4px !important;
  font-size: 10px !important;
}

.vouchers-table.compact-table thead th {
  max-width: 100px;
}

.vouchers-table.compact-table thead tr {
  height: 42px;
}

.reduceHeight {
  height: 36px !important;
  line-height: 1.2 !important;
}

.reduceHeight td {
  padding: 5px 6px !important;
  font-size: 10px !important;
  vertical-align: middle !important;
}

.reduceHeight .cell-content {
  max-height: 24px !important;
  overflow: hidden !important;
  text-align: center !important;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.reduceHeight .icon-btn {
  padding: 2px !important;
  width: 24px !important;
  height: 24px !important;
}

.reduceHeight .icon-btn svg {
  width: 12px !important;
  height: 12px !important;
}

.reduceHeight input[type="checkbox"] {
  width: 12px !important;
  height: 12px !important;
}

/* Floating button */
.floating-add-btn {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #0d6efd;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
  transition: all 0.2s;
  z-index: 1000;
}

.floating-add-btn:hover {
  background: #0b5ed7;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(13, 110, 253, 0.4);
}
        `}
      </style>

      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className="content-area">
          <SubHeader />

          <div className="financial-container">
            <div className="financial-card">
              {/* SINGLE COMPACT CONTROL ROW  */}
              <div className="flex px-4 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-tr-lg rounded-tl-lg text-white py-2">
                {/* Left - Entries Selector */}
                <div className="controls-left">
                  <div className="entries-selector">
                    <label className="text-white">
                      Show
                      <select
                        value={perPage}
                        onChange={handlePerPageChange}
                        className="entries-select"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                      entries
                    </label>
                  </div>
                </div>

                {/* Center - Search and Action Buttons */}
                <div className="controls-center">
                  <div className="flex items-center gap-2">
                    {/* Search Input - Compact */}
                    <div className="relative">
                      <Search
                        size={14}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        placeholder="Search vouchers..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="pl-8 pr-8 py-1 border border-gray-300 rounded text-sm text-gray-700"
                        style={{
                          width: "200px",
                          height: "28px",
                          fontSize: "12px",
                        }}
                      />
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2"
                        >
                          <X
                            size={14}
                            className="text-gray-400 hover:text-gray-600"
                          />
                        </button>
                      )}
                    </div>

                    {/* Action Buttons - Compact */}
                    <button
                      className="rounded border-none flex bg-blue-300 text-white hover:bg-blue-700 p-2"
                      onClick={fetchVouchers}
                      disabled={loading}
                      title="Refresh vouchers list"
                    >
                      <RefreshCw
                        size={14}
                        className={loading ? "spin-animation" : ""}
                      />
                    </button>

                    <button
                      className="rounded border-none flex align-center bg-red-300 text-white hover:bg-red-500 p-2"
                      onClick={deleteByAdminBillTable}
                      title="Delete all vouchers"
                    >
                      <Trash2 size={14} />
                    </button>

                    <button
                      className="rounded border-none gap-2 flex items-center text-left bg-red-300 text-white hover:bg-red-500 px-2 py-1"
                      onClick={deleteSelectedMembers}
                      disabled={selectedVouchers.length === 0}
                      title={`Delete ${selectedVouchers.length} selected voucher(s)`}
                    >
                      <Trash2 size={14} />
                      <span style={{ fontSize: "11px" }}>
                        {selectedVouchers.length}
                      </span>
                      <CheckSquare size={14} />
                    </button>
                  </div>
                </div>

                {/* Right - Pagination */}
                <div className="controls-right">
                  {!loading && totalItems > 0 && (
                    <span className="entries-info text-white">
                      {(currentPage - 1) * perPage + 1}-
                      {Math.min(currentPage * perPage, totalItems)} of{" "}
                      {totalItems}
                    </span>
                  )}

                  <div className="flex gap-2 p-2">
                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Prev
                    </button>

                    {renderPageNumbers()}

                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="financial-body">
                {loading ? (
                  <div className="financial-state">
                    <RefreshCw size={48} className="spin-animation" />
                    <h3>Loading Vouchers</h3>
                    <p>Please wait while we fetch the data...</p>
                  </div>
                ) : error ? (
                  <div className="financial-state error-state">
                    <AlertCircle size={48} />
                    <h3>Unable to Load Data</h3>
                    <p>{String(error)}</p>
                    <button
                      onClick={fetchVouchers}
                      style={{
                        marginTop: "20px",
                        padding: "10px 20px",
                        background: "#0d6efd",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Try Again
                    </button>
                  </div>
                ) : vouchers.length === 0 ? (
                  <div className="financial-state">
                    <div
                      style={{
                        background: "#f8fafc",
                        padding: "40px",
                        borderRadius: "16px",
                        textAlign: "center",
                        maxWidth: "500px",
                        margin: "0 auto",
                      }}
                    >
                      <FileX size={64} color="#94a3b8" />
                      <h3
                        style={{
                          margin: "20px 0 10px 0",
                          color: "#334155",
                          fontSize: "20px",
                          fontWeight: "600",
                        }}
                      >
                        No Vouchers Found
                      </h3>
                      <p
                        style={{
                          color: "#64748b",
                          fontSize: "14px",
                          margin: 0,
                          lineHeight: "1.5",
                        }}
                      >
                        {searchTerm
                          ? "No vouchers match your search criteria."
                          : "There are no vouchers available for this society."}
                      </p>
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          style={{
                            marginTop: "20px",
                            padding: "8px 20px",
                            background: "#f1f5f9",
                            border: "1px solid #e2e8f0",
                            borderRadius: "6px",
                            color: "#475569",
                            cursor: "pointer",
                            fontSize: "14px",
                          }}
                        >
                          Clear Search
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="financial-table-container">
                    <table className="vouchers-table compact-table">
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              onChange={selectAllRows}
                              checked={
                                selectedVouchers.length > 0 &&
                                selectedVouchers.length ===
                                  paginatedData.filter((item) => item.voucherId)
                                    .length
                              }
                            />
                          </th>
                          <th>Sr No</th>
                          <th>Company</th>
                          <th>Voucher Code</th>
                          <th>Employee</th>
                          <th>Society</th>
                          <th>Unit No</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((row, i) => {
                          const globalIndex =
                            (currentPage - 1) * perPage + i + 1;
                          const isRowDeleting = loadingIds.has(row.voucherId);
                          const isSelected = selectedVouchers.includes(
                            row.voucherId,
                          );

                          return (
                            <tr
                              key={row.voucherId || globalIndex}
                              className={`reduceHeight ${
                                isSelected ? "selected-row" : ""
                              }`}
                            >
                              <td>
                                {row.voucherId && (
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() =>
                                      handleRowSelect(row.voucherId)
                                    }
                                  />
                                )}
                              </td>
                              <td>{globalIndex}</td>
                              <td
                                className="cell-ellipsis"
                                title={row.companyName}
                              >
                                {row.companyName || "-"}
                              </td>
                              <td>{row.voucherCode || "-"}</td>
                              <td
                                className="cell-ellipsis"
                                title={row.employeeName}
                              >
                                {row.employeeName || "-"}
                              </td>
                              <td
                                className="cell-ellipsis"
                                title={row.societyName}
                              >
                                {row.societyName || "-"}
                              </td>
                              <td>{row.unitNo || "-"}</td>
                              <td>{row.startDate || "-"}</td>
                              <td>{row.endDate || "-"}</td>
                              <td>
                                <div className="action-buttons">
                                  <button
                                    className="icon-btn edit"
                                    title="View Voucher"
                                    onClick={() => handleViewItem(row)}
                                  >
                                    <Eye size={14} />
                                  </button>
                                  <button
                                    className="icon-btn edit"
                                    title="Edit Voucher"
                                    onClick={() => handleEditClick(row)}
                                    disabled={isRowDeleting}
                                  >
                                    <Edit size={14} />
                                  </button>
                                  <button
                                    className="icon-btn edit"
                                    title="Download Voucher"
                                    onClick={() => {
                                      Swal.fire(
                                        "Info",
                                        "Download voucher functionality coming soon",
                                        "info",
                                      );
                                    }}
                                  >
                                    <Download size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Floating Add Button */}
          <button
            onClick={handleAddClick}
            className="floating-add-btn"
            title="Add New Voucher"
          >
            <Plus size={24} />
          </button>
        </main>
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => setIsEditModalOpen(false)}
        className="edit-modal"
        overlayClassName="edit-modal-overlay"
      >
        <div className="edit-modal-content">
          <div className="edit-modal-header">
            <h2 className="edit-modal-title">Edit Voucher</h2>
            <button
              className="edit-modal-close"
              onClick={() => setIsEditModalOpen(false)}
            >
              <X size={20} />
            </button>
          </div>

          <div className="edit-modal-body">
            {editFormData && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSaveEdit();
                }}
                className="edit-form"
              >
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Company</label>
                    <input
                      type="text"
                      name="companyName"
                      value={editFormData.companyName || ""}
                      onChange={handleFormChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Voucher Code</label>
                    <input
                      type="text"
                      name="voucherCode"
                      value={editFormData.voucherCode || ""}
                      onChange={handleFormChange}
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Employee</label>
                    <input
                      type="text"
                      name="employeeName"
                      value={editFormData.employeeName || ""}
                      onChange={handleFormChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Unit No</label>
                    <input
                      type="text"
                      name="unitNo"
                      value={editFormData.unitNo || ""}
                      onChange={handleFormChange}
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-group full-width">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={editFormData.startDate || ""}
                    onChange={handleFormChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group full-width">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={editFormData.endDate || ""}
                    onChange={handleFormChange}
                    className="form-input"
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="btn-cancel"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-save">
                    Save Changes
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Vouchers;
